import { useState, useEffect, useCallback } from "react";
import AuthProvider from "../../../common/services/auth/AuthProvider/Index";

export interface ShippingAddressData {
  customerName: string;
  addressLine: string;
  city: string;
  stateProvinceCode: string;
  postalCode: string;
  phone: string;
}

interface UseShippingAddressOptions {
  /** Reload address when window gains focus */
  reloadOnFocus?: boolean;
  /** Initial enabled state */
  initialEnabled?: boolean;
}

interface UseShippingAddressReturn {
  addressData: ShippingAddressData;
  setAddressData: (data: Partial<ShippingAddressData>) => void;
  fieldsEnabled: boolean;
  loadShippingAddress: () => Promise<void>;
}

export const useShippingAddress = (
  options: UseShippingAddressOptions = {}
): UseShippingAddressReturn => {
  const { reloadOnFocus = true, initialEnabled = false } = options;

  const [addressData, setAddressData] = useState<ShippingAddressData>({
    customerName: "",
    addressLine: "",
    city: "",
    stateProvinceCode: "",
    postalCode: "",
    phone: "",
  });
  const [fieldsEnabled, setFieldsEnabled] = useState<boolean>(initialEnabled);

  const updateAddressData = useCallback((data: Partial<ShippingAddressData>) => {
    setAddressData((prev) => ({ ...prev, ...data }));
  }, []);

  const loadShippingAddress = useCallback(async () => {
    try {
      const identity = await AuthProvider.getIdentity();
      if (identity) {
        if (identity.shipping_address) {
          const addr = identity.shipping_address;
          const fullName =
            identity.given_name && identity.family_name
              ? `${identity.given_name} ${identity.family_name}`
              : identity.given_name || identity.family_name || "";

          setAddressData({
            customerName: fullName,
            addressLine: Array.isArray(addr.addressLine)
              ? addr.addressLine[0]
              : addr.addressLine || "",
            city: addr.city || "",
            stateProvinceCode: addr.stateProvinceCode || "",
            postalCode: addr.postalCode || "",
            phone: addr.phone || "",
          });
          setFieldsEnabled(true);
        } else {
          setFieldsEnabled(false);
        }
      }
    } catch (error) {
      console.error("Error loading shipping address from profile:", error);
      setFieldsEnabled(false);
    }
  }, []);

  useEffect(() => {
    loadShippingAddress();

    if (reloadOnFocus) {
      const handleFocus = () => {
        loadShippingAddress();
      };

      window.addEventListener("focus", handleFocus);

      return () => {
        window.removeEventListener("focus", handleFocus);
      };
    }
  }, [loadShippingAddress, reloadOnFocus]);

  return {
    addressData,
    setAddressData: updateAddressData,
    fieldsEnabled,
    loadShippingAddress,
  };
};

export default useShippingAddress;
